import type { ScheduleBlock } from "../api/types.js";
import { atMostRow, dateChips, eachTimeRow, endsRow, firstRow, ruleIcon, ruleLine, type SchedKV } from "../app/schedule-words.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";

/**
 * DCTA-209 F55 (09-scheduled.md §2.1, shared.css 13.1) — the schedule block on a card to sign: the rule in the bank's words,
 * the first dates as chips (labels, not buttons: no role, no tab stop — the chat's tappable chips live in the stream), then
 * Each time · Ends · At most as key–value rows (they stack in large type). A group named "When it goes out". Every string
 * is a signed field put into words (`app/schedule-words.ts`); every one goes in as text. `note` (DCTA-221, row 27p): the
 * bank's sentence beside the payload — never a signed field — under the rule line.
 */
export function scheduleBlock(b: ScheduleBlock, note?: string): HTMLElement {
  const chips = dateChips(b);
  const kv: HTMLElement[] = [];
  const row = (r: SchedKV): HTMLElement =>
    el("div", {}, [el("span", { class: "k" }, [r.k]), el("span", { class: "v" }, [r.v, ...(r.small === undefined ? [] : [el("small", {}, [r.small])])])]);
  const first = firstRow(b);
  if (first !== undefined) kv.push(row(first));
  const each = eachTimeRow(b);
  if (each !== undefined) kv.push(row(each));
  const ends = endsRow(b);
  if (ends !== undefined) kv.push(row(ends));
  const most = atMostRow(b);
  if (most !== undefined) kv.push(el("div", {}, [el("span", { class: "k" }, [most.k]), el("span", { class: "v" }, [`${most.count} · `, el("span", { class: "money" }, [most.total])])]));
  return el("div", { class: "sched", role: "group", aria: { label: "When it goes out" } }, [
    el("p", { class: "rule" }, [icon(ruleIcon(b)), el("span", {}, [ruleLine(b)])]),
    // DCTA-221 (row 27p): the bank's sentence when the time the customer said is not kept — beside the payload, as text.
    ...(note === undefined || note === "" ? [] : [el("p", { class: "note" }, [note])]),
    ...(chips.length === 0
      ? []
      : [
          el(
            "ul",
            { class: "dates", aria: { label: "First payments" } },
            // What is seen ("Sun 1 Nov", the weekday in ink-2) is hidden from a screen reader, which hears the full words.
            chips.map((c) => el("li", {}, [el("span", { aria: { hidden: "true" } }, [el("span", {}, [c.weekday]), ` ${c.day}`]), el("span", { class: "vis-hidden" }, [c.long])])),
          ),
        ]),
    ...(kv.length === 0 ? [] : [el("div", { class: "kv" }, kv)]),
  ]);
}
